import React from 'react';
import SiteHeader from './SiteHeader';
import { puckImageField as imageField } from '../../../puck/fields/puckImageField';
import { puckColorField } from '../../../puck/fields/createPuckColorField';
import { puckSwitchField } from '../../../puck/fields/puckSwitchField';
import { puckPxSpacingField } from '../../../puck/fields/puckPxSpacingField';
import SiteHeaderLinkStyleField from './SiteHeaderLinkStyleField';
import { MOCK_NAV_LINKS } from './menuMockData';
import LinkListField from '../shared/LinkListField';
import { resolveButtonBorder } from '../../../../shared/boxes/button/buttonStyles';
import { puckBorderField } from '../../../puck/fields/puckBorderField';
import { puckButtonLinkField } from '../../../puck/fields/ButtonLinkField';
import { puckTextRolesField } from '../../../puck/fields/TextRolesField';
import { puckAdvancedSectionField, puckSectionField } from '../../../puck/fields/puckSectionField';

const PAGE_BAR_FIELDS = [
  'pageBarNumbers', 'pageBarTextStyle', 'pageBarBackground', 'pageBarActiveBackground',
  'pageBarActiveColor', 'pageBarNumberColor', 'pageBarBorders', 'pageBarUnderline',
];

const CTA_FIELDS = ['ctaLink', 'ctaColor', 'ctaSize', 'ctaBorder', 'ctaTextStyles'];

const siteHeaderConfig = {
  label: "Menu d'en tête",
  defaultProps: {
    logoUrl: '',
    // Nom du menu : texte riche (édité inline sur le canvas, toolbar au survol).
    logoName: 'Nom du menu',
    logoHeight: '2.25rem', // = 36px, la hauteur réellement rendue par défaut

    backgroundColor: '#ffffff',
    // Style commun des liens (toolbar : police, taille, B/I/U, couleur).
    // fontSize 0.875rem = 14px, la taille réellement rendue par défaut (.link).
    linkStyle: {
      fontFamily: '', fontSize: '0.875rem', bold: false, italic: false, underline: false, color: '',
    },
    accentColor: '',
    menuLinks: 'pages',
    customLinks: [],
    hideOnScroll: false,
    navVariant: 'left-logo',
    sticky: true,
    activeHighlight: false,
    paddingX: '2.5rem', // marge horizontale par défaut = 40px
    navLinks: [],
    // Style « panneau coulissant » : le bouton flottant se règle à part.
    burgerBackground: '#ffffff',
    burgerColor: '',
    burgerShadow: true,
    // Bouton d'action (« Nous contacter »…), désactivé par défaut. Le libellé
    // s'écrit sur le canvas, comme celui de la box Bouton.
    cta: false,
    ctaLabel: 'Nous contacter',
    ctaColor: '',
    ctaSize: 'sm',
    ctaTextStyles: null,
    // Barre des pages : une case par page sous le menu, désactivée par défaut.
    pageBar: false,
    pageBarNumbers: true,
    pageBarBackground: '',
    pageBarActiveBackground: '',
    pageBarActiveColor: '',
    pageBarNumberColor: '',
    pageBarBorders: true,
    pageBarUnderline: true,
    pageBarTextStyle: {
      fontFamily: '', fontSize: '', bold: false, italic: false, underline: false, color: '',
    },
  },
  fields: {
    _section1: puckSectionField('Contenu'),
    logoUrl: imageField('Logo'),
    logoHeight: puckPxSpacingField('Hauteur du logo'),
    linkStyle: {
      type: 'custom',
      label: 'Style des liens',
      render: (props) => <SiteHeaderLinkStyleField {...props} />,
    },
    menuLinks: {
      type: 'select',
      label: 'Liens du menu',
      layout: 'stacked',
      options: [
        { label: 'Pages et leurs titres', value: 'pages' },
        { label: 'Pages seules', value: 'pages-flat' },
        { label: 'Liens choisis', value: 'custom' },
        { label: 'Aucun lien', value: 'none' },
      ],
    },

    customLinks: {
      type: 'custom',
      label: 'Liens',
      render: (props) => <LinkListField {...props} />,
    },

    _section2: puckSectionField('Style'),
    navVariant: {
      type: 'select',
      label: 'Style de menu',
      /* Options trop longues pour la demi-largeur. */
      layout: 'stacked',
      options: [
        { label: 'Logo à gauche, liens à droite', value: 'left-logo' },
        { label: 'Logo centré, liens dessous', value: 'centered' },
        { label: 'Liens répartis (symétrique)', value: 'split' },
        { label: 'Panneau coulissant (☰ en haut à gauche)', value: 'drawer-left' },
      ],
    },
    backgroundColor: puckColorField('Couleur de fond', { allowTransparent: true }),
    accentColor: puckColorField('Couleur de mise en avant', {
      allowTransparent: true,
      description: 'Couleur du lien au survol de la souris, et du lien de la page affichée.',
    }),
    burgerBackground: puckColorField('Fond du bouton flottant', {
      allowTransparent: true,
      description: 'Transparent : l\'icône est posée à nu sur le site.',
    }),
    burgerColor: puckColorField('Couleur de l\'icône du bouton', { allowTransparent: true }),

    _section3: puckSectionField('Affichage'),
    sticky: puckSwitchField(
      'Menu fixe au défilement',
      'Le menu reste visible en haut quand on descend la page.',
    ),
    hideOnScroll: puckSwitchField(
      'Masquer en descendant',
      'Le menu se retire quand on descend la page et revient dès qu’on remonte.',
    ),
    activeHighlight: puckSwitchField(
      'Mettre en avant le lien de la page affichée',
      'Applique la couleur de mise en avant (ci-dessus) au lien de la page en cours.',
    ),

    _sectionCta: puckSectionField('Bouton d\'action'),
    cta: puckSwitchField('Afficher un bouton d\'action', 'Son libellé s\'écrit sur le menu.'),
    ctaLink: puckButtonLinkField(),
    ctaColor: puckColorField('Couleur du bouton'),
    ctaSize: {
      type: 'radio',
      label: 'Taille',
      options: [
        { label: 'Petit', value: 'sm' },
        { label: 'Moyen', value: 'md' },
        { label: 'Grand', value: 'lg' },
      ],
    },
    ctaBorder: puckBorderField('Bordures', { readDefaults: () => resolveButtonBorder() }),
    ctaTextStyles: puckTextRolesField([{ key: 'label', label: 'Libellé' }]),

    _sectionPageBar: puckSectionField('Barre des pages'),
    pageBar: puckSwitchField(
      'Afficher la barre des pages',
      'Une case par page sous le menu, la page affichée mise en avant.',
    ),
    pageBarNumbers: puckSwitchField('Numéroter les pages'),
    pageBarTextStyle: {
      type: 'custom',
      label: 'Style du texte',
      render: (props) => <SiteHeaderLinkStyleField {...props} />,
    },
    pageBarBackground: puckColorField('Fond de la barre', { allowTransparent: true }),
    pageBarActiveBackground: puckColorField('Fond de la page affichée', { allowTransparent: true }),
    pageBarActiveColor: puckColorField('Couleur de la page affichée', { emptyIsInherited: true }),
    pageBarNumberColor: puckColorField('Couleur du numéro de la page affichée', { emptyIsInherited: true }),
    pageBarBorders: puckSwitchField('Séparer les cases par un trait'),
    pageBarUnderline: puckSwitchField('Souligner la page affichée'),

    _section4: puckAdvancedSectionField(),
    paddingX: puckPxSpacingField('Marge horizontale de la barre'),
    burgerShadow: puckSwitchField(
      'Ombre sous le bouton flottant',
      'Détache le bouton du contenu qui défile dessous.',
    ),
  },
  /* Sans barre, « menu fixe au défilement » et « marge horizontale de la barre »
     ne veulent plus rien dire : le ☰ est toujours visible, et il n'y a pas de
     barre à marger. */
  /* Deux jeux de réglages s'excluent : la barre (fixe au défilement, marge
     horizontale) n'existe pas en panneau coulissant, et le bouton flottant
     n'existe que là. On filtre sans réordonner — l'ordre des clés fait les
     sections de l'inspecteur. */
  resolveFields: (data, { fields }) => {
    const isDrawer = data.props.navVariant === 'drawer-left';
    const dropped = isDrawer
      ? ['sticky', 'hideOnScroll', 'paddingX', ...PAGE_BAR_FIELDS, '_sectionPageBar', 'pageBar']
      : ['burgerBackground', 'burgerColor', 'burgerShadow'];
    // Barre éteinte : seul l'interrupteur reste, ses réglages ne diraient rien.
    if (!data.props.pageBar) dropped.push(...PAGE_BAR_FIELDS);
    if (!data.props.cta) dropped.push(...CTA_FIELDS);
    if (data.props.menuLinks !== 'custom') dropped.push('customLinks');
    if (!data.props.sticky) dropped.push('hideOnScroll');

    const kept = Object.fromEntries(
      Object.entries(fields).filter(([key]) => !dropped.includes(key)),
    );

    // Sans barre, la couleur de fond n'a qu'un endroit où se poser : le panneau.
    if (isDrawer) {
      kept.backgroundColor = { ...kept.backgroundColor, label: 'Couleur du panneau' };
    }
    return kept;
  },
  render: (props) => (
    <SiteHeader
      {...props}
      /* Éditeur uniquement : le viewer rend SiteHeader en direct avec ses vrais
         navLinks. Ici la section est isolée → on injecte des liens factices
         pour visualiser le menu + le sous-menu au survol. */
      navLinks={props.navLinks?.length ? props.navLinks : MOCK_NAV_LINKS}
      activeSlug={MOCK_NAV_LINKS[0].slug}
    />
  ),
};

export default siteHeaderConfig;
